import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { mensajeAmigable } from '../utils/errores';
import { numeroSeguro } from '../utils/validacion';
import { obtenerCuentas } from '../services/cuentasService';
import { convertirCotizacion } from '../services/cotizacionesService';
import { registrarAbono } from '../services/porCobrarService';
import { registrarComisionBancaria } from '../services/gastosService';
import { obtenerComisionPct } from '../services/negociosService';
import { calcularComision, COMISION_POS_PCT } from '../utils/comisiones';
import { fechaLocal } from '../utils/fecha';
import { formatoMoneda } from '../utils/servicios';
import CampoNumero from './CampoNumero';

// Paso 4: convierte una cotización APROBADA en venta y permite COBRAR CON VARIAS
// FORMAS DE PAGO (Efectivo, BAC, POS/Ficohsa…). Cada forma entra a su cuenta;
// lo que no se pague ahora queda en "Por cobrar". Reutiliza los abonos y la
// comisión POS que ya usa la pantalla de Por cobrar.

const P = 'var(--color-primario,#DB2777)';
const input = { width: '100%', padding: '10px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' };
const label = { display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 600, color: '#374151' };

export default function ConvertirCotizacionModal({ cotizacion: c, onCerrar, onConvertida }) {
  const { negocio } = useAuth();
  const { mostrarToast } = useToast();
  const moneda = negocio?.moneda || 'L';
  const fmt = (v) => formatoMoneda(v, moneda);
  const total = Number(c.total) || 0;

  const [cuentas, setCuentas] = useState(null);
  const [comisionPct, setComisionPct] = useState(COMISION_POS_PCT);
  // pagos: cada forma de pago = { monto, cuentaId, pos }. Empiezan VACÍOS: el
  // total es la "nota" de arriba y ella reparte desde cero (con atajo "el resto").
  const [pagos, setPagos] = useState([{ monto: '', cuentaId: '', pos: false }]);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    obtenerCuentas()
      .then((cs) => {
        setCuentas(cs || []);
        if (cs && cs[0]) setPagos((p) => p.map((x) => x.cuentaId ? x : { ...x, cuentaId: cs[0].id }));
      })
      .catch(() => setCuentas([]));
    obtenerComisionPct().then(setComisionPct).catch(() => {});
  }, []);

  const setPago = (i, campo, valor) => setPagos((ps) => ps.map((p, j) => j === i ? { ...p, [campo]: valor } : p));
  const agregarPago = () => setPagos((ps) => [...ps, { monto: '', cuentaId: cuentas?.[0]?.id || '', pos: false }]);
  const quitarPago = (i) => setPagos((ps) => ps.filter((_, j) => j !== i));

  const pagadoAhora = pagos.reduce((s, p) => s + (numeroSeguro(p.monto) || 0), 0);
  const restante = Math.round((total - pagadoAhora) * 100) / 100;
  const faltaCliente = restante > 0.009 && !c.cliente_id;
  const excede = pagadoAhora - total > 0.009;
  // Lo que falta por repartir SIN contar la línea i (para el atajo "usar el resto").
  const restoPara = (i) => {
    const otros = pagos.reduce((s, p, j) => (j === i ? s : s + (numeroSeguro(p.monto) || 0)), 0);
    return Math.max(Math.round((total - otros) * 100) / 100, 0);
  };

  const confirmar = async () => {
    if (excede) { mostrarToast('La suma de los pagos no puede ser mayor al total.', 'error'); return; }
    for (const p of pagos) {
      if ((numeroSeguro(p.monto) || 0) > 0 && !p.cuentaId) {
        mostrarToast('Elige a qué cuenta entra cada pago.', 'error'); return;
      }
    }
    setGuardando(true);
    let cxcId;
    try {
      cxcId = await convertirCotizacion(c.id);
    } catch (e) {
      const m = String(e?.message || '');
      let amigable = mensajeAmigable(e);
      if (m.includes('YA_CONVERTIDA')) amigable = 'Esta cotización ya fue convertida en venta. Búscala en Ventas o en Por cobrar.';
      else if (m.includes('COTIZACION_NO_APROBADA')) amigable = 'Solo puedes convertir una cotización que esté Aprobada.';
      else if (m.includes('STOCK_INSUFICIENTE')) amigable = 'No hay suficiente stock de un producto de la cotización.';
      else if (m.includes('COTIZACION_SIN_ITEMS')) amigable = 'La cotización no tiene renglones.';
      mostrarToast(amigable, 'error');
      setGuardando(false);
      return;
    }
    // La venta ya quedó creada en "Por cobrar". Aplica cada forma de pago como
    // abono (y su comisión POS si aplica). Si algo falla, el resto queda cobrable.
    try {
      for (const p of pagos) {
        const monto = numeroSeguro(p.monto) || 0;
        if (monto <= 0) continue;
        await registrarAbono({ cxcId, monto, cuentaId: p.cuentaId });
        if (p.pos) {
          const comision = calcularComision(monto, comisionPct);
          if (comision > 0) {
            await registrarComisionBancaria({
              monto: comision, cuentaId: p.cuentaId, fecha: fechaLocal(),
              detalle: `POS / Link · ${c.numero || 'cotización'}`,
            });
          }
        }
      }
      mostrarToast('¡Cotización convertida en venta! 🎉', 'exito');
    } catch (e) {
      mostrarToast('La venta se creó, pero un pago no se registró. Termínalo en 💰 Por cobrar.', 'error', 8000);
    } finally {
      onConvertida && onConvertida();
      onCerrar();
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: '14px' }} onClick={guardando ? undefined : onCerrar}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: '16px', padding: '22px', width: '480px', maxWidth: '100%', maxHeight: '92vh', overflowY: 'auto', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
          <h2 style={{ margin: 0, color: P, fontSize: '19px' }}>💰 Convertir en venta</h2>
          <button onClick={onCerrar} disabled={guardando} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#9CA3AF' }}>✕</button>
        </div>
        <p style={{ margin: '0 0 14px', fontSize: '13px', color: '#6B7280' }}>{c.numero} · {c.cliente_nombre || 'Sin cliente'}</p>

        {/* Nota fija: el total de la venta (no se edita). El cobro se reparte abajo. */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'color-mix(in srgb, var(--color-primario,#DB2777) 8%, white)', border: `1px solid ${P}`, borderRadius: '12px', padding: '12px 14px', marginBottom: '14px' }}>
          <span style={{ fontSize: '14px', fontWeight: 700, color: '#374151' }}>💵 Total a cobrar</span>
          <span style={{ fontSize: '20px', fontWeight: 800, color: P }}>{fmt(total)}</span>
        </div>

        {cuentas && cuentas.length === 0 ? (
          <p style={{ fontSize: '13.5px', color: '#B91C1C', margin: '8px 0' }}>Primero crea al menos una cuenta en la sección "Cuentas".</p>
        ) : (
          <>
            <label style={label}>¿Cómo lo cobras? Reparte el total (lo que no cobres ahora queda en Por cobrar)</label>
            {pagos.map((p, i) => (
              <div key={i} style={{ border: '1px solid #EEF0F3', borderRadius: '12px', padding: '10px', marginBottom: '8px', background: '#FAFAFB' }}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                  <div style={{ flex: 1 }}>
                    <CampoNumero etiqueta="Monto" valor={p.monto} prefijo={moneda} min={0} deshabilitado={guardando}
                      onChange={(v) => setPago(i, 'monto', v)} placeholder="0.00" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ ...label, marginBottom: '5px' }}>Cuenta</label>
                    <select value={p.cuentaId} disabled={guardando || !cuentas} onChange={(e) => setPago(i, 'cuentaId', e.target.value)} style={input}>
                      {(cuentas || []).map((cu) => <option key={cu.id} value={cu.id}>{cu.nombre}</option>)}
                    </select>
                  </div>
                  {pagos.length > 1 && (
                    <button type="button" onClick={() => quitarPago(i)} disabled={guardando}
                      style={{ padding: '9px 11px', borderRadius: '8px', border: '1px solid #FCA5A5', background: '#fff', color: '#DC2626', cursor: 'pointer' }}>🗑️</button>
                  )}
                </div>
                {restoPara(i) > 0 && (
                  <button type="button" onClick={() => setPago(i, 'monto', String(restoPara(i).toFixed(2)))} disabled={guardando}
                    style={{ background: 'none', border: 'none', color: P, fontSize: '12px', fontWeight: 700, cursor: 'pointer', padding: '6px 0 0', textDecoration: 'underline' }}>
                    usar el resto ({fmt(restoPara(i))})
                  </button>
                )}
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '7px', marginTop: '8px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={p.pos} disabled={guardando} onChange={(e) => setPago(i, 'pos', e.target.checked)} style={{ marginTop: '2px', width: '15px', height: '15px', flexShrink: 0 }} />
                  <span style={{ fontSize: '12.5px', color: '#374151' }}>
                    💳 Cobrado con POS o Link de pago <span style={{ color: '#1D4ED8' }}>(−{comisionPct}% comisión)</span>
                    {p.pos && numeroSeguro(p.monto) > 0 && (
                      <span style={{ display: 'block', color: '#6B7280' }}>Se rebajará {fmt(calcularComision(numeroSeguro(p.monto) || 0, comisionPct))} de esa cuenta como comisión.</span>
                    )}
                  </span>
                </label>
              </div>
            ))}
            <button type="button" onClick={agregarPago} disabled={guardando}
              style={{ marginBottom: '14px', padding: '8px 14px', borderRadius: '8px', border: `1px solid ${P}`, background: '#fff', color: P, fontWeight: 700, fontSize: '13px', cursor: 'pointer' }}>
              ➕ Agregar otra forma de pago
            </button>

            <div style={{ background: '#F9FAFB', borderRadius: '12px', padding: '12px 14px', marginBottom: '14px' }}>
              <Fila etq="Se cobra ahora" val={fmt(pagadoAhora)} color={excede ? '#B91C1C' : '#047857'} />
              <Fila etq="Queda en Por cobrar" val={fmt(Math.max(restante, 0))} fuerte />
            </div>

            {excede && <Aviso texto="La suma de los pagos es mayor al total. Ajústala." />}
            {faltaCliente && <Aviso texto="Va a quedar un saldo por cobrar, pero esta cotización no tiene cliente. Aparecerá como 'Sin cliente' (mejor asígnale uno en la cotización para poder cobrarle después)." tono="info" />}
          </>
        )}

        <button onClick={confirmar} disabled={guardando || excede || (cuentas && cuentas.length === 0) || !cuentas}
          style={{ width: '100%', padding: '14px', background: (guardando || excede || !cuentas || cuentas.length === 0) ? '#9CA3AF' : P, color: '#fff', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: 'bold', cursor: guardando ? 'wait' : 'pointer' }}>
          {guardando ? '⏳ Convirtiendo…' : '✓ Confirmar venta'}
        </button>
        <p style={{ textAlign: 'center', margin: '10px 0 0', fontSize: '11.5px', color: '#9CA3AF' }}>
          Se crea la venta, se descuenta el inventario de los productos y cada pago entra a su cuenta.
        </p>
      </div>
    </div>
  );
}

function Fila({ etq, val, fuerte, color }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontWeight: fuerte ? 700 : 400, color: color || (fuerte ? '#111827' : '#6B7280'), fontSize: '14px' }}>
      <span>{etq}</span><span>{val}</span>
    </div>
  );
}

function Aviso({ texto, tono = 'warn' }) {
  const c = tono === 'info' ? { bg: '#EFF6FF', fg: '#1E40AF' } : { bg: '#FEF3C7', fg: '#92400E' };
  return <p style={{ background: c.bg, color: c.fg, padding: '10px 12px', borderRadius: '10px', fontSize: '12.5px', fontWeight: 600, margin: '0 0 12px' }}>⚠️ {texto}</p>;
}
